import axios from '@/lib/axios'
import { useCefsStore } from '@/stores/cefs'
import { useQuery } from '@tanstack/vue-query'
import _ from 'lodash'
import { storeToRefs } from 'pinia'
import { reactive, watch } from 'vue'

import { PaginatedResponse } from '@/interfaces/paginatedResponse'

import { CefsFilters } from '@/modules/trips/interfaces/CefFilters'
import { Cef } from '@/modules/trips/interfaces/cef'

interface Props {
	immediate?: boolean
}

interface Filters extends CefsFilters {
	page?: number
}

const getCefs = async (
	params: Filters,
	page: string | number
): Promise<PaginatedResponse<Cef>> => {
	const { data } = await axios.get<PaginatedResponse<Cef>>(
		'/api/internal/cefs',
		{
			params: {
				travelerName: params.travelerName === '' ? null : params.travelerName,
				confirmation: params.confirmation === '' ? null : params.confirmation,
				name: params.name === '' ? null : params.name,
				creatorName: params.creatorName === '' ? null : params.creatorName,
				company: params.company,
				cef: params.cef,
				supplierName: params.supplierName === '' ? null : params.supplierName,
				folio: params.folio === 0 ? null : params.folio,
				order: params.order === 'Todos' ? null : params.order,
				status: params.status === 0 ? null : params.status,
				progressStage:
					params.progressStage === 'Todos' ? null : params.progressStage,
				hasLetter: params.hasLetter === 'Todos' ? null : params.hasLetter,
				service: params.service === 0 ? null : params.service,
				channel: params.channel === 0 ? null : params.channel,
				dates: params.dates,
				creationDates: params.creationDates,
				page,
			},
		}
	)

	return data
}

const useCefs = (filters: Filters, { immediate = true }: Props = {}) => {
	const { setCefs, setPagination, setPage } = useCefsStore()
	const { cefs, page, pagination } = storeToRefs(useCefsStore())

	const { data, isError, isLoading, refetch, isRefetching, isFetching } =
		useQuery(['cefs'], () => getCefs(filters, page.value), {
			retry: false,
		})

	watch(
		data,
		(response) => {
			if (response) {
				setCefs(response.data)
				setPagination(_.omit(response, ['data']))
			}
		},
		{ immediate }
	)

	return {
		data,
		cefs,
		pagination,
		page,
		isError,
		isLoading,
		isRefetching,
		isFetching,

		setPage,
		searchParams() {
			refetch()
		},
	}
}

export default useCefs
